import React, { useState, useEffect } from 'react';

const AvailabilityDisplay = ({ provider, userTimezone }) => {
  const [viewMode, setViewMode] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarWeeks, setCalendarWeeks] = useState([]);

  const daysOfWeek = [
    { value: 0, label: 'Sunday', short: 'Sun' },
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' }
  ];

  useEffect(() => {
    generateCalendarData();
  }, [selectedDate, provider]);

  const generateCalendarData = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    // Get first day of the month and last day
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Get first day of calendar (might be from previous month)
    const firstDayOfCalendar = new Date(firstDayOfMonth);
    firstDayOfCalendar.setDate(firstDayOfCalendar.getDate() - firstDayOfMonth.getDay());
    
    // Generate calendar weeks
    const weeks = [];
    const currentDate = new Date(firstDayOfCalendar);
    
    for (let week = 0; week < 6; week++) {
      const weekDays = [];
      for (let day = 0; day < 7; day++) {
        const date = new Date(currentDate);
        const availability = getAvailabilityForDate(date);
        
        weekDays.push({
          date: new Date(date),
          isCurrentMonth: date.getMonth() === month,
          isToday: date.toDateString() === new Date().toDateString(),
          availability: availability
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(weekDays);
      
      // Stop if we've gone past the current month and filled a complete week
      if (currentDate.getMonth() !== month && week >= 4) break;
    }
    
    setCalendarWeeks(weeks);
  };

  const getAvailabilityForDate = (date) => {
    const dayOfWeek = date.getDay();
    const dateStr = date.toISOString().split('T')[0];
    const slots = [];

    // Check one-off availability
    if (provider.oneOffAvailability) {
      const oneOffSlot = provider.oneOffAvailability.find(slot => 
        slot.isActive && slot.date === dateStr
      );
      if (oneOffSlot) {
        slots.push({
          ...oneOffSlot,
          type: 'one-off'
        });
      }
    }

    // Check recurring availability (only if no one-off for this date)
    if (slots.length === 0) {
      let recurringSlots = [];
      
      // Check for new weeklyAvailability format first
      if (provider.weeklyAvailability) {
        const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayKey = dayMap[dayOfWeek];
        const dayData = provider.weeklyAvailability[dayKey];
        
        if (dayData && dayData.available) {
          recurringSlots = dayData.timeSlots.map(slot => ({
            ...slot,
            dayOfWeek: dayOfWeek,
            isActive: true
          }));
        }
      } else if (provider.recurringAvailability) {
        recurringSlots = provider.recurringAvailability.filter(slot => 
          slot.isActive && slot.dayOfWeek === dayOfWeek
        );
      }
      
      slots.push(...recurringSlots.map(slot => ({
        ...slot,
        type: 'recurring'
      })));
    }

    return slots;
  };

  const formatTimeForDisplay = (time, timezone = null) => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':');
    const timeDate = new Date();
    timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    try {
      return timeDate.toLocaleTimeString('en-US', {
        timeZone: timezone || provider.timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      // Fallback if timezone conversion fails
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    }
  };

  const convertTimeToUserTimezone = (time, date, fromTimezone, toTimezone) => {
    if (!time || !fromTimezone || !toTimezone || fromTimezone === toTimezone) {
      return time;
    }

    try {
      const [hours, minutes] = time.split(':');
      const tempDate = new Date(date);
      tempDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      // Convert to user timezone
      const userTime = new Date(tempDate.toLocaleString('en-US', { timeZone: toTimezone }));
      
      return userTime.toTimeString().slice(0, 5);
    } catch (error) {
      return time; // Fallback to original time
    }
  };

  const getUpcomingAvailability = () => {
    const upcomingSlots = [];
    const today = new Date();
    const nextTwoWeeks = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

    // Add one-off availability
    if (provider.oneOffAvailability) {
      provider.oneOffAvailability.forEach(slot => {
        if (slot.isActive) {
          const slotDate = new Date(slot.date);
          if (slotDate >= today && slotDate <= nextTwoWeeks) {
            upcomingSlots.push({
              ...slot,
              date: slotDate,
              type: 'one-off'
            });
          }
        }
      });
    }

    // Add recurring availability for next two weeks
    let recurringSlots = [];
    
    // Get recurring slots from either format
    if (provider.weeklyAvailability) {
      const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      
      dayMap.forEach((dayKey, dayOfWeek) => {
        const dayData = provider.weeklyAvailability[dayKey];
        if (dayData && dayData.available) {
          dayData.timeSlots.forEach(slot => {
            recurringSlots.push({
              ...slot,
              dayOfWeek: dayOfWeek,
              isActive: true
            });
          });
        }
      });
    } else if (provider.recurringAvailability) {
      recurringSlots = provider.recurringAvailability;
    }
    
    for (let i = 0; i < 14; i++) {
      const checkDate = new Date(today.getTime() + i * 24 * 60 * 60 * 1000);
      const dayOfWeek = checkDate.getDay();
      
      // Skip if there's already a one-off slot for this date
      const hasOneOff = upcomingSlots.some(slot => 
        slot.type === 'one-off' && 
        slot.date.toDateString() === checkDate.toDateString()
      );
      
      if (!hasOneOff) {
        recurringSlots.forEach(slot => {
          if (slot.isActive && slot.dayOfWeek === dayOfWeek) {
            upcomingSlots.push({
              ...slot,
              date: new Date(checkDate),
              type: 'recurring'
            });
          }
        });
      }
    }

    // Sort by date and time
    upcomingSlots.sort((a, b) => {
      const dateA = new Date(a.date.toDateString() + ' ' + a.startTime);
      const dateB = new Date(b.date.toDateString() + ' ' + b.startTime);
      return dateA - dateB;
    });

    return upcomingSlots.slice(0, 10); // Show next 10 slots
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const upcomingSlots = getUpcomingAvailability();

  return (
    <div className="availability-display">
      <div className="availability-header">
        <h3>Availability</h3>
        <div className="view-toggle">
          <button 
            className={viewMode === 'calendar' ? 'active' : ''}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
          <button 
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      {viewMode === 'calendar' && (
        <div className="calendar-view">
          <div className="calendar-header">
            <button onClick={() => navigateMonth(-1)} className="nav-btn">‹</button>
            <h4>
              {selectedDate.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h4>
            <button onClick={() => navigateMonth(1)} className="nav-btn">›</button>
          </div>

          <div className="calendar-grid">
            <div className="calendar-days-header">
              {daysOfWeek.map(day => (
                <div key={day.value} className="day-header">
                  {day.short}
                </div>
              ))}
            </div>

            {calendarWeeks.map((week, weekIndex) => (
              <div key={weekIndex} className="calendar-week">
                {week.map((day, dayIndex) => (
                  <div 
                    key={dayIndex} 
                    className={`calendar-day ${
                      !day.isCurrentMonth ? 'other-month' : ''
                    } ${day.isToday ? 'today' : ''} ${
                      day.availability.length > 0 ? 'has-availability' : ''
                    }`}
                  >
                    <span className="day-number">{day.date.getDate()}</span>
                    {day.availability.length > 0 && (
                      <div className="availability-indicators">
                        {day.availability.map((slot, slotIndex) => (
                          <div 
                            key={slotIndex}
                            className={`availability-dot ${slot.type}`}
                            title={`${slot.type === 'one-off' ? 'Special' : 'Regular'} availability: ${formatTimeForDisplay(slot.startTime)} - ${formatTimeForDisplay(slot.endTime)}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="calendar-legend">
            <div className="legend-item">
              <div className="availability-dot recurring"></div>
              <span>Regular availability</span>
            </div>
            <div className="legend-item">
              <div className="availability-dot one-off"></div>
              <span>Special availability</span>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="list-view">
          <h4>Upcoming Availability</h4>
          {upcomingSlots.length === 0 ? (
            <div className="no-availability">
              <p>No upcoming availability in the next two weeks.</p>
            </div>
          ) : (
            <div className="availability-list">
              {upcomingSlots.map((slot, index) => (
                <div key={index} className="availability-item">
                  <div className="availability-date">
                    <div className="date-main">
                      {slot.date.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="date-year">
                      {slot.date.getFullYear()}
                    </div>
                  </div>
                  
                  <div className="availability-time">
                    <div className="time-range">
                      {formatTimeForDisplay(slot.startTime)} - {formatTimeForDisplay(slot.endTime)}
                    </div>
                    <div className="time-provider-tz">
                      {provider.timezone ? `${provider.timezone.replace('_', ' ')} time` : 'Provider time'}
                    </div>
                    {userTimezone && userTimezone !== provider.timezone && (
                      <div className="time-user-tz">
                        Your time: {formatTimeForDisplay(slot.startTime, userTimezone)} - {formatTimeForDisplay(slot.endTime, userTimezone)}
                      </div>
                    )}
                  </div>
                  
                  <div className="availability-type">
                    <span className={`type-badge ${slot.type}`}>
                      {slot.type === 'one-off' ? 'Special' : 'Regular'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AvailabilityDisplay;